import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { toast } from "sonner";
import { MagnifyingGlass, DownloadSimple, ImageSquare } from "@phosphor-icons/react";

const STATUSES = ["placed", "in_production", "quality_check", "dispatched", "delivered", "cancelled"];
const STATUS_COLORS = {
  placed: "bg-amber-100 text-amber-800",
  in_production: "bg-blue-100 text-blue-800",
  quality_check: "bg-purple-100 text-purple-800",
  dispatched: "bg-indigo-100 text-indigo-800",
  delivered: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-rose-100 text-rose-800",
};

const csvEscape = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/admin/orders/enriched")
      .then(r => setOrders(r.data))
      .catch(() => toast.error("Failed to load orders"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const update = async (id, s) => {
    try { await api.patch(`/orders/${id}/status`, { status: s }); toast.success("Status updated"); load(); }
    catch { toast.error("Update failed"); }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return orders.filter(o => {
      if (filter !== "all" && o.status !== filter) return false;
      if (!needle) return true;
      return (
        o.id.toLowerCase().includes(needle) ||
        (o.customer?.name || "").toLowerCase().includes(needle) ||
        (o.customer?.email || "").toLowerCase().includes(needle) ||
        (o.customer?.company || "").toLowerCase().includes(needle) ||
        (o.product_type || "").toLowerCase().includes(needle) ||
        (o.brief_industry || "").toLowerCase().includes(needle)
      );
    });
  }, [orders, q, filter]);

  const counts = useMemo(() => {
    const c = { all: orders.length };
    STATUSES.forEach(s => { c[s] = orders.filter(o => o.status === s).length; });
    return c;
  }, [orders]);

  const exportCSV = () => {
    const rows = [
      ["Order ID", "Date", "Customer", "Email", "Company", "Product", "Industry", "Qty", "Amount (INR)", "Status", "Source", "Design Prompt", "Delivery Address"],
      ...filtered.map(o => [
        o.id, new Date(o.created_at).toISOString(), o.customer?.name, o.customer?.email,
        o.customer?.company, o.product_type, o.brief_industry, o.quantity, o.total_amount,
        o.status, o.design_source, o.design_prompt, o.delivery_address,
      ]),
    ];
    const csv = rows.map(r => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `talaio-orders-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} orders`);
  };

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="pf-label mb-3">Admin · Orders</div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">Order pipeline.</h1>
          </div>
          <button
            onClick={exportCSV}
            disabled={filtered.length === 0}
            data-testid="admin-orders-export-csv"
            className="pf-btn-secondary inline-flex items-center gap-2 disabled:opacity-40"
          >
            <DownloadSimple size={16} /> Export CSV
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-5 max-w-xl">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pf-text-3)]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by customer, email, order ID, product…"
            data-testid="admin-orders-search"
            className="pf-input pl-10 w-full"
          />
        </div>

        {/* Status filter tabs */}
        <div className="flex gap-2 mb-6 flex-wrap" data-testid="admin-orders-filters">
          {[["all", "All"], ...STATUSES.map(s => [s, s.replace("_", " ")])].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              data-testid={`filter-${key}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold border-2 transition-colors ${
                filter === key
                  ? "bg-[var(--pf-text)] text-white border-[var(--pf-text)]"
                  : "bg-white border-[var(--pf-border)] text-[var(--pf-text-2)] hover:border-[var(--pf-text)]"
              }`}
            >
              {label} <span className="opacity-60">({counts[key] || 0})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="border border-dashed border-[var(--pf-border)] p-16 text-center text-[var(--pf-text-2)]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-[var(--pf-border)] p-16 text-center text-[var(--pf-text-2)]" data-testid="no-admin-orders">
            {orders.length === 0 ? "No orders yet." : "No orders match this filter."}
          </div>
        ) : (
          <div className="border border-[var(--pf-border)] bg-white">
            {filtered.map(o => (
              <div key={o.id} className="grid grid-cols-12 gap-4 p-4 border-b last:border-0 border-[var(--pf-border)] items-center hover:bg-[var(--pf-bg)] transition-colors" data-testid={`admin-order-${o.id}`}>
                {/* Thumbnail */}
                <div className="col-span-12 sm:col-span-1">
                  {o.design_thumbnail ? (
                    <img
                      src={`data:image/png;base64,${o.design_thumbnail}`}
                      alt="design"
                      className="w-14 h-14 object-cover rounded border border-[var(--pf-border)]"
                      data-testid={`admin-order-thumb-${o.id}`}
                    />
                  ) : (
                    <div className="w-14 h-14 rounded border border-dashed border-[var(--pf-border)] flex items-center justify-center text-[var(--pf-text-3)]">
                      <ImageSquare size={22} />
                    </div>
                  )}
                </div>

                {/* Customer */}
                <div className="col-span-12 sm:col-span-3 min-w-0">
                  <div className="font-semibold text-sm truncate" data-testid={`admin-order-customer-${o.id}`}>{o.customer?.name || "Unknown"}</div>
                  <div className="text-xs text-[var(--pf-text-3)] truncate">{o.customer?.email}</div>
                  {o.customer?.company && <div className="text-xs text-[var(--pf-text-3)] truncate">{o.customer.company}</div>}
                </div>

                {/* Product */}
                <div className="col-span-6 sm:col-span-2 min-w-0">
                  <div className="font-semibold text-sm capitalize truncate">{o.product_type.replace("_", " ")}</div>
                  <div className="text-xs text-[var(--pf-text-3)]">{o.quantity} × units</div>
                  {o.brief_industry && <div className="text-xs text-[var(--pf-text-3)] truncate">· {o.brief_industry}</div>}
                </div>

                {/* Amount */}
                <div className="col-span-6 sm:col-span-2">
                  <div className="font-mono text-sm font-bold">₹{o.total_amount.toLocaleString("en-IN")}</div>
                  <div className="text-xs text-[var(--pf-text-3)]">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
                  {o.design_source && (
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--pf-text-3)] mt-0.5">{o.design_source}</div>
                  )}
                </div>

                {/* Status */}
                <div className="col-span-8 sm:col-span-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${STATUS_COLORS[o.status] || "bg-gray-100 text-gray-800"}`}>
                      {o.status.replace("_", " ")}
                    </span>
                  </div>
                  <select
                    value={o.status}
                    onChange={(e) => update(o.id, e.target.value)}
                    data-testid={`admin-status-${o.id}`}
                    className="mt-2 border-2 border-[var(--pf-border)] px-2 py-1 text-xs uppercase tracking-wide font-bold bg-white w-full"
                  >
                    {STATUSES.map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </div>

                {/* View */}
                <div className="col-span-4 sm:col-span-1 text-right">
                  <Link
                    to={`/orders/${o.id}`}
                    data-testid={`admin-order-view-${o.id}`}
                    className="text-xs font-bold uppercase tracking-widest text-[var(--pf-brand)] hover:underline"
                  >
                    View →
                  </Link>
                  <div className="font-mono text-[10px] text-[var(--pf-text-3)] mt-1">#{o.id.slice(0, 8).toUpperCase()}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-xs text-[var(--pf-text-3)] mt-3">
          Showing {filtered.length} of {orders.length} orders
        </div>
      </div>
    </div>
  );
}
